import Cookies from 'js-cookie';
import { createContext, useContext } from 'react';

import {
  AiApi,
  ApiApi,
  Configuration,
  RestAuthApi,
  TeamsApi,
  UtilsApi,
} from '../generated-api';
import { AppEnvironment } from './appEnvironmentContext';

export const buildAPI =
  (appEnvironment = AppEnvironment.Web) =>
  () => {
    const config = new Configuration(
      appEnvironment === 'office'
        ? {
            // Always use the same domain for the API so that the session auth works (the local dev server will redirect those appropriately)
            basePath: window.location.origin,
            baseOptions: {
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': Cookies.get('csrftoken'),
                // X-Host will be one of: 'excel', 'powerpoint'
                // See https://learn.microsoft.com/en-us/javascript/api/office/office.hosttype?view=common-js-preview#fields
                'X-Host': Office?.context?.host?.toString()?.toLowerCase(),
                // X-Platform will be one of: 'Android', 'iOS', 'Mac', 'OfficeOnline', 'PC', 'Universal'
                // See https://learn.microsoft.com/en-us/javascript/api/office/office.platformtype?view=common-js-preview#fields
                'X-Platform': Office?.context?.platform,
              },
            },
          }
        : {
            // Always point to the API of the current app
            basePath: window.location.origin,
            baseOptions: {
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': Cookies.get('csrftoken'),
                'X-Host': 'web',
              },
            },
          }
    );

    const aiApi = new AiApi(config);
    const apiApi = new ApiApi(config);
    const authApi = new RestAuthApi(config);
    const teamsApi = new TeamsApi(config);
    const utilsApi = new UtilsApi(config);

    return {
      ai: {
        generateSankeyFromImages: aiApi.generateSankeyFromImages.bind(aiApi),
        sankeyGenerationStatus: aiApi.sankeyGenerationStatus.bind(aiApi),
      },
      currentUser: {
        retrieve: authApi.restAuthUserRetrieve.bind(authApi),
        partialUpdate: authApi.restAuthUserPartialUpdate.bind(authApi),
        signInViaEmail: authApi.restAuthLoginCreate.bind(authApi),
        signInViaGoogle: authApi.restAuthGoogleCreate.bind(authApi),
        signInViaMicrosoft: authApi.restAuthMicrosoftCreate.bind(authApi),
        signUpViaEmail: authApi.restAuthRegistrationCreate.bind(authApi),
        signOut: authApi.restAuthLogoutCreate.bind(authApi),
      },
      sankeys: {
        list: apiApi.apiSankeysList.bind(apiApi),
        retrieve: apiApi.apiSankeysRetrieve.bind(apiApi),
        create: apiApi.apiSankeysCreate.bind(apiApi),
        partialUpdate: apiApi.apiSankeysPartialUpdate.bind(apiApi),
        destroy: apiApi.apiSankeysDestroy.bind(apiApi),
      },
      subscriptions: {
        activeProductsList: apiApi.activeProductsList.bind(apiApi),
        createCheckoutSession: apiApi.createCheckoutSession.bind(apiApi),
        subscribe: apiApi.subscribe.bind(apiApi),
      },
      teams: {
        partialUpdate: teamsApi.teamsPartialUpdate.bind(teamsApi),
      },
      utils: {
        convertPdfToPngs: utilsApi.convertPdfToPngs.bind(utilsApi),
        downloadImage: utilsApi.downloadImage.bind(utilsApi),
      },
    };
  };

export type API = ReturnType<typeof buildAPI>;

export type ApiState = {
  api: API;
};

export const ApiContext = createContext<ApiState>({
  api: buildAPI(undefined),
});

export const useApiContext = () => useContext(ApiContext);
